import { lazy, Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { useSocketEvent } from '@/lib/useSocket';
import { sound } from '@/lib/sound';
import type { ChallengeView } from './ChallengeDialog';

// La tarjeta (avatar, banderas, piezas) solo se descarga al llegar un reto.
const ChallengeCard = lazy(() => import('./ChallengeCard'));

/**
 * Avisos de reto, estés donde estés en la aplicación.
 *
 * Aparecen en una esquina y se aceptan o se rechazan sin salir de la página:
 * un reto que solo se viera entrando en su sección serviría de poco.
 */
export function ChallengeToasts() {
  const [items, setItems] = useState<ChallengeView[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const navigate = useNavigate();

  useSocketEvent<ChallengeView>('challenge:incoming', (challenge) => {
    setItems((current) => [challenge, ...current.filter((item) => item.id !== challenge.id)]);
    sound.playNotify();
  });

  useSocketEvent<{ id: string }>('challenge:canceled', (data) => {
    setItems((current) => current.filter((item) => item.id !== data.id));
  });

  const dismiss = (id: string) =>
    setItems((current) => current.filter((item) => item.id !== id));

  const accept = async (id: string) => {
    setBusy(id);
    const result = await api
      .post<{ game: { id: string } }>(`/challenges/${id}/accept`)
      .catch(() => null);
    setBusy(null);
    dismiss(id);
    if (result) navigate(`/partida/${result.game.id}`);
  };

  const decline = async (id: string) => {
    dismiss(id);
    await api.post(`/challenges/${id}/decline`).catch(() => undefined);
  };

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-[calc(var(--nav-h)+12px)] right-3 left-3 z-50 space-y-2 sm:left-auto sm:w-[360px] lg:bottom-4">
      <Suspense fallback={null}>
        {items.map((challenge) => (
          <ChallengeCard
            key={challenge.id}
            challenge={challenge}
            busy={busy === challenge.id}
            onAccept={() => void accept(challenge.id)}
            onDecline={() => void decline(challenge.id)}
            onDismiss={() => dismiss(challenge.id)}
          />
        ))}
      </Suspense>
    </div>
  );
}
